/**
 * Workflow fixtures file containing React Flow configuration, node types, edge styles,
 * and default values for workflow components.
 */
// Declaring our custom edge type/style to react-flow
import type { EdgeMarker } from "reactflow"
import { MarkerType } from "reactflow"

import { v4 as uuidv4 } from "uuid"

import { getTheme } from "../../../hooks/UseTheme"
import { Operators } from "../../../types/custom/projects"
import type { FilterCondition } from "../../../types/custom/rules"
import { CustomEdgeLabel } from "../components/canvas/CustomEdgeLabel"
import { CalculatorNode } from "../components/nodes/CalculatorNode"
import { FilterNode } from "../components/nodes/FilterNode"
import { LoopEndNode } from "../components/nodes/LoopEndNode"
import { LoopStartNode } from "../components/nodes/LoopStartNode"
import { ProgramNode } from "../components/nodes/ProgramNode"
import { ProjectNode } from "../components/nodes/ProjectNode"
import { RulesetNode } from "../components/nodes/RulesetNode"
import { ScorecardsetNode } from "../components/nodes/ScorecardsetNode"
import { ScriptNode } from "../components/nodes/ScriptNode"
import { StartNode } from "../components/nodes/StartNode"
import { TaglistNode } from "../components/nodes/TaglistNode"
import { AddBlockNode } from "../components/nodes/base/AddBlockNode"
import { LabeledNode } from "../components/nodes/base/LabeledNode"

/**
 * Custom edge types configuration for React Flow.
 * Maps edge type names to their corresponding components.
 */
export const edgeTypes = {
  custom: CustomEdgeLabel,
}

const theme = getTheme()

/**
 * Generic custom edge styles configuration.
 * Defines the appearance of workflow edges including markers and colors.
 */
export const markerEnd: EdgeMarker = {
  type: MarkerType.ArrowClosed,
  width: 40,
  height: 20,
  color: theme.palette.secondary.border.default,
  orient: "auto",
}
/**
 * Edge styles configuration for React Flow edges.
 * Defines visual styling for different edge states and types.
 */
export const edgeStyles = {
  strokeWidth: 2,
  stroke: theme.palette.secondary.border.default,
  color: theme.palette.secondary.border.default,
}

// declaring our custom nodes types/styles to react-flow
/**
 * Node types configuration for React Flow.
 * Maps node type names to their corresponding React components.
 */
export const nodeTypes = {
  StartNode,
  RulesetNode,
  TaglistNode,
  ScorecardsetNode,
  ProgramNode,
  ProjectNode,
  CalculatorNode,
  AddBlockNode,
  LabeledNode,
  FilterNode,
  LoopStartNode,
  LoopEndNode,
  ScriptNode,
}

/**
 * Default empty filter condition used as a template for new filter rules.
 * Provides the initial structure for filter conditions in workflow nodes.
 */
export const EMPTY_FILTER_CONDITION: FilterCondition = {
  feature: "",
  valueOrFeature: "Value",
  value: "",
  type: "string",
  secondValue: "",
  secondFeature: "",
  id: uuidv4(),
  andOr: "and",
  operator: Operators["="],
}

/**
 * Array of node types that do not display labels in the workflow canvas.
 * These nodes have their own internal labeling mechanisms.
 */
export const NODES_WITHOUT_LABEL = [
  "ProjectNode",
  "StartNode",
  "EndResultNode",
  "TaglistNode",
  "ScorecardsetNode",
  "CalculatorNode",
  "ScriptNode",
  "MarketPlaceScriptNode",
  "ProgramNode",
  "LoopStartNode",
  "LoopEndNode",
]

/**
 * Static analysis script template for workflow script nodes.
 * Provides a default Python script structure for data analysis workflows.
 */
export const StaticAnalysisScript = `# importing request from flask is for input data retrieval
from flask import request

"""
Retrieve credentials from the 'KONAN_CREDS' dictionary within the provided data.
"""
def get_credentials(data):
	return data.get('KONAN_CREDS')

"""
Main function that retrieves input data from the workflow.

The function name must be 'main' for it to be executed by the workflow.
"""
def main():
	# This is how to get input data sent from workflow
	data = request.get_json()
	credentials_data =  get_credentials(data)

	# retrieving score from the input
	score = data.get("score")

	# retrieving secret_score from script credentials
	credential_score = credentials_data.get("credential_score")

	return {"score": score, "credential_score": credential_score, "i_score":1234} # returned dict`

/**
 * Spanish version of the static analysis script template.
 * Provides the same functionality as StaticAnalysisScript but with Spanish comments.
 */
export const StaticAnalysisScriptEs = `# importar request desde flask es para la recuperación de datos de entrada
from flask import request

"""
Recupera las credenciales del diccionario 'KONAN_CREDS' dentro de los datos proporcionados.
"""
def get_credentials(data):
	return data.get('KONAN_CREDS')

"""
Función principal que recupera los datos de entrada del flujo de trabajo.

El nombre de la función debe ser 'main' para que sea ejecutada por el flujo de trabajo.
"""
def main():
	# Así es como se obtienen los datos de entrada enviados desde el flujo de trabajo
	data = request.get_json()
	credentials_data =  get_credentials(data)

	# recuperando la puntuación de la entrada
	score = data.get("score")

	# recuperando la puntuación secreta de las credenciales del script
	credential_score = credentials_data.get("credential_score")

	return {"score": score, "credential_score": credential_score, "i_score":1234} # dict retornado`
